import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Chip, Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { PieceRateOperationService } from '../../services/piece-rate.service';
import { IPieceRateOperation, IPieceRateOperationCreateRequest, IPieceRateOperationUpdateRequest } from '../../interfaces/pieceRate.interface';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import PieceRateOperationForm from './PieceRateOperationForm';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { toLocalIsoDate } from '../../utils/dateUtils';

type RateStatus = 'Active' | 'Superseded' | 'Scheduled' | 'Inactive';

/**
 * Mirrors the backend rate resolver: per code (case-insensitive) the live version is the active
 * row with the greatest effectiveFrom on or before today. Older active rows are superseded rate
 * revisions, later ones are scheduled -- otherwise a revision reads like a duplicate operation.
 */
export const rateStatuses = (ops: IPieceRateOperation[], today: string = toLocalIsoDate()): Map<string, RateStatus> => {
    const currentByCode = new Map<string, string>();
    ops.filter(o => o.isActive && o.effectiveFrom <= today).forEach(o => {
        const key = o.code.toLowerCase();
        const cur = currentByCode.get(key);
        if (cur === undefined || o.effectiveFrom > cur) currentByCode.set(key, o.effectiveFrom);
    });
    const statuses = new Map<string, RateStatus>();
    ops.forEach(o => {
        if (!o.isActive) statuses.set(o.id, 'Inactive');
        else if (o.effectiveFrom > today) statuses.set(o.id, 'Scheduled');
        else statuses.set(o.id, o.effectiveFrom === currentByCode.get(o.code.toLowerCase()) ? 'Active' : 'Superseded');
    });
    return statuses;
};

const STATUS_COLOR: Record<RateStatus, 'primary' | 'default' | 'info'> = {
    Active: 'primary', Superseded: 'default', Scheduled: 'info', Inactive: 'default',
};

/**
 * Piece-rate operation catalog (T3a), modelled on WageGradeManagement.tsx. Referenced master data
 * is never deleted -- Deactivate/Activate only (feedback_deactivate_not_delete_referenced_entities).
 */
const PieceRateOperationList: React.FC = () => {
    const [operations, setOperations] = useState<IPieceRateOperation[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingOperation, setEditingOperation] = useState<IPieceRateOperation | null>(null);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();
    const canManage = usePermission(PERMISSIONS.PIECE_RATE_MANAGE);
    const statuses = rateStatuses(operations);

    const loadOperations = async () => {
        if (!institutionId) return;
        try {
            const data = await PieceRateOperationService.getByInstitution(institutionId);
            setOperations(data);
        } catch (error) {
            showApiError(error, 'Failed to load piece-rate operations');
        }
    };

    useEffect(() => { loadOperations(); }, [institutionId]);

    const handleAdd = () => {
        setEditingOperation(null);
        setDialogOpen(true);
    };

    const handleEdit = (operation: IPieceRateOperation) => {
        setEditingOperation(operation);
        setDialogOpen(true);
    };

    const handleSave = async (data: IPieceRateOperationCreateRequest | IPieceRateOperationUpdateRequest) => {
        try {
            if (editingOperation) {
                await PieceRateOperationService.update(editingOperation.id, data as IPieceRateOperationUpdateRequest);
                showSuccess('Piece-rate operation updated');
            } else {
                await PieceRateOperationService.create({ ...(data as IPieceRateOperationCreateRequest), institutionId });
                showSuccess('Piece-rate operation created');
            }
            setDialogOpen(false);
            loadOperations();
        } catch (error) {
            showApiError(error, 'Failed to save piece-rate operation');
        }
    };

    const handleToggleActive = async (operation: IPieceRateOperation) => {
        try {
            await PieceRateOperationService.update(operation.id, {
                displayName: operation.displayName,
                isActive: !operation.isActive,
                notes: operation.notes || undefined,
            });
            showSuccess(operation.isActive ? 'Operation deactivated' : 'Operation activated');
            loadOperations();
        } catch (error) {
            showApiError(error, 'Failed to update operation');
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="pieceRate.operations.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Piece-Rate Operations</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }} data-help-section="pieceRate.operations.toolbar">
                    {isSystemAdmin && (
                        <SearchableSelect
                            options={institutions}
                            value={institutionId}
                            onChange={(id) => setInstitutionId(id)}
                            getOptionLabel={inst => inst.name}
                            label="Select Institution"
                            data-help="pieceRate.operations.institution"
                            size="small"
                            fullWidth={false}
                            sx={{ minWidth: 220 }}
                        />
                    )}
                    {canManage && (
                        <Button data-help="pieceRate.operations.add" variant="contained" color="primary" onClick={handleAdd} disabled={!institutionId}>
                            Add Operation
                        </Button>
                    )}
                </Box>
            </Box>

            {operations.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No piece-rate operations are configured for this institution yet.
                </Alert>
            )}

            <TableContainer component={Paper} data-help-section="pieceRate.operations.table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="pieceRate.operations.col.code">Code</TableCell>
                            <TableCell data-help="pieceRate.operations.col.name">Display Name</TableCell>
                            <TableCell align="right" data-help="pieceRate.operations.col.rate">Rate Per Piece</TableCell>
                            <TableCell data-help="pieceRate.operations.col.effectiveFrom">Effective From</TableCell>
                            <TableCell data-help="pieceRate.operations.col.status">Status</TableCell>
                            {canManage && <TableCell align="right" data-help="pieceRate.operations.col.actions">Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {operations.map((op) => (
                            <TableRow key={op.id}>
                                <TableCell>{op.code}</TableCell>
                                <TableCell>{op.displayName}</TableCell>
                                <TableCell align="right">{op.ratePerPiece.toFixed(2)}</TableCell>
                                <TableCell>{op.effectiveFrom}</TableCell>
                                <TableCell>
                                    <Chip label={statuses.get(op.id) ?? 'Inactive'}
                                          color={STATUS_COLOR[statuses.get(op.id) ?? 'Inactive']}
                                          variant={statuses.get(op.id) === 'Superseded' ? 'outlined' : 'filled'} size="small" />
                                </TableCell>
                                {canManage && (
                                    <TableCell align="right">
                                        <IconButton onClick={() => handleEdit(op)} size="small" aria-label="edit" data-help="pieceRate.operations.action.edit">
                                            <EditIcon />
                                        </IconButton>
                                        {statuses.get(op.id) !== 'Superseded' && (
                                            <Button size="small" data-help="pieceRate.operations.action.toggle" onClick={() => handleToggleActive(op)}>
                                                {op.isActive ? 'Deactivate' : 'Activate'}
                                            </Button>
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        {operations.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={canManage ? 6 : 5} align="center">No piece-rate operations found</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <PieceRateOperationForm
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSave}
                operation={editingOperation}
            />
        </Box>
    );
};

export default PieceRateOperationList;
